import { ShieldAlert, Package as PackageIcon } from "lucide-react";

/**
 * ParcelConditionSection
 * Controlled section for capturing physical condition of a parcel during Inward/Outward.
 * All fields default to safe values (Excellent / Intact / No) and are optional at the API level.
 *
 * value: {
 *   package_condition, damage_type, seal_condition,
 *   water_damage, leakage, tampered, missing_labels,
 *   fragile_sticker, handle_with_care,
 * }
 */
export const DEFAULT_CONDITION = {
  package_condition: "Excellent",       // Excellent | Good | Damaged
  damage_type: "",                      // Torn | Wet | Crushed | Broken Seal | Open
  seal_condition: "Intact",             // Intact | Broken
  water_damage: false,
  leakage: false,
  tampered: false,
  missing_labels: false,
  fragile_sticker: false,
  handle_with_care: false,
};

const CONDITION_OPTS  = ["Excellent", "Good", "Damaged"];
const DAMAGE_OPTS     = ["Torn", "Wet", "Crushed", "Broken Seal", "Open"];
const SEAL_OPTS       = ["Intact", "Broken"];

const TOGGLE_FIELDS = [
  { key: "water_damage",     label: "Water Damage" },
  { key: "leakage",          label: "Leakage" },
  { key: "tampered",         label: "Tampered" },
  { key: "missing_labels",   label: "Missing Labels" },
  { key: "fragile_sticker",  label: "Fragile Sticker Present" },
  { key: "handle_with_care", label: "Handle With Care" },
];

export default function ParcelConditionSection({ value = {}, onChange, prefix = "cond" }) {
  const v = { ...DEFAULT_CONDITION, ...(value || {}) };
  const set = (k, val) => onChange({ ...v, [k]: val });

  const damaged = v.package_condition === "Damaged" || v.seal_condition === "Broken"
    || v.water_damage || v.leakage || v.tampered || v.missing_labels;

  return (
    <div
      className={`border rounded-md p-4 space-y-4 ${damaged ? "border-primary bg-red-50/60" : "border-border bg-muted/20"}`}
      data-testid={`${prefix}-section`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {damaged
            ? <ShieldAlert size={14} className="text-primary" />
            : <PackageIcon size={14} className="text-muted-foreground" />}
          <div className="overline">Parcel Condition</div>
        </div>
        {damaged && (
          <span
            data-testid={`${prefix}-damage-flag`}
            className="text-[10px] uppercase tracking-widest bg-primary text-primary-foreground font-bold px-2 py-0.5 rounded"
          >
            Attention · damaged
          </span>
        )}
      </div>

      {/* Row 1: 3 pickers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <SegmentPicker
          label="Package Condition"
          testid={`${prefix}-package-condition`}
          options={CONDITION_OPTS}
          value={v.package_condition}
          onChange={(x) => set("package_condition", x)}
        />
        <SegmentPicker
          label="Seal Condition"
          testid={`${prefix}-seal-condition`}
          options={SEAL_OPTS}
          value={v.seal_condition}
          onChange={(x) => set("seal_condition", x)}
        />
        <SegmentPicker
          label="Damage Type"
          testid={`${prefix}-damage-type`}
          options={DAMAGE_OPTS}
          value={v.damage_type}
          onChange={(x) => set("damage_type", v.damage_type === x ? "" : x)}
          optional
          disabled={v.package_condition !== "Damaged"}
        />
      </div>

      {/* Row 2: 6 Yes/No toggles */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-2">
          Additional flags
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {TOGGLE_FIELDS.map(f => (
            <YesNoToggle
              key={f.key}
              testid={`${prefix}-${f.key}`}
              label={f.label}
              value={!!v[f.key]}
              onChange={(x) => set(f.key, x)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function SegmentPicker({ label, testid, options, value, onChange, optional, disabled }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-1">
        {label}{optional && <span className="text-muted-foreground/70 font-normal normal-case tracking-normal"> · optional</span>}
      </div>
      <div className={`inline-flex flex-wrap gap-1 border border-border rounded-md bg-white p-0.5 ${disabled ? "opacity-50 pointer-events-none" : ""}`} data-testid={testid}>
        {options.map(opt => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              data-testid={`${testid}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => onChange(opt)}
              className={`text-[11px] px-2.5 py-1.5 rounded-[4px] font-semibold transition-colors ${
                active
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-muted"
              }`}
            >{opt}</button>
          );
        })}
      </div>
    </div>
  );
}

function YesNoToggle({ label, value, onChange, testid }) {
  return (
    <div
      className={`flex items-center justify-between gap-2 border rounded-md px-3 py-2 bg-white ${
        value ? "border-primary" : "border-border"
      }`}
      data-testid={testid}
    >
      <span className="text-xs font-medium text-foreground">{label}</span>
      <div className="inline-flex border border-border rounded-md overflow-hidden">
        <button
          type="button"
          onClick={() => onChange(true)}
          data-testid={`${testid}-yes`}
          className={`text-[10px] px-2 py-1 font-bold uppercase tracking-widest ${
            value ? "bg-primary text-primary-foreground" : "bg-white text-muted-foreground"
          }`}
        >Yes</button>
        <button
          type="button"
          onClick={() => onChange(false)}
          data-testid={`${testid}-no`}
          className={`text-[10px] px-2 py-1 font-bold uppercase tracking-widest border-l border-border ${
            !value ? "bg-foreground text-background" : "bg-white text-muted-foreground"
          }`}
        >No</button>
      </div>
    </div>
  );
}
